/**
 * 我獨自升級設定(REQ-20260523;REQ-20260729001 調整):
 *  - Redmine URL / Project Identifier / API key
 *  - GitLab Repo URL / Personal Access Token
 *  - 主機 Claude Code 的模型 / effort(REQ-20260802001;分析與改 code 各一組)
 *
 * 後端對 `system_setting` key=`smart-adjust.integrations`(isEncrypted=true)。
 * GET 不回 apiKey 真值,只回 apiKeyConfigured;PUT 接收 apiKey 留空 = 保留原值。
 *
 * REQ-20260729001 移除了「AI 模型(我獨自升級專用)」區塊:那組是 **OpenAI** 的
 * model + API key,改用主機 Claude Code 後不再有任何程式讀取,留著只會讓使用者
 * 誤以為填了會生效。
 * REQ-20260802001 加回來的是不同的東西 —— Claude Code 自己的模型別名與 effort,
 * 仍然不涉及任何 API key(認證走主機訂閱)。
 */
import axios from 'axios';
import { useEffect, useState } from 'react';
import type {
  ApiSuccess,
  SmartAdjustClaudeConfig,
  SmartAdjustClaudeRunConfig,
  SmartAdjustIntegrationsView,
} from '@app/shared';
import { apiClient } from '../../lib/api-client';

/**
 * 模型固定給**別名**:別名自動跟上該系列最新模型,寫死完整 id(claude-opus-5)
 * 出新版就過期,而過期只會在真的送出升級時才炸。
 */
const MODEL_OPTIONS: { value: SmartAdjustClaudeRunConfig['model']; label: string }[] = [
  { value: '', label: '預設(依主機 Claude Code 設定)' },
  { value: 'opus', label: 'opus — 最強,C 級改 code 建議' },
  { value: 'sonnet', label: 'sonnet — 平衡' },
  { value: 'haiku', label: 'haiku — 最快最省' },
];

const EFFORT_OPTIONS: { value: SmartAdjustClaudeRunConfig['effort']; label: string }[] = [
  { value: '', label: '預設(不指定)' },
  { value: 'low', label: 'low' },
  { value: 'medium', label: 'medium' },
  { value: 'high', label: 'high' },
  { value: 'xhigh', label: 'xhigh' },
  { value: 'max', label: 'max' },
];

const SELECT_CLASS =
  'border rounded px-2 py-1.5 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-blue-400';

/** 一組(模型 + effort)的兩個下拉。analyze / edit 共用同一個元件 */
function ClaudeRunFields({
  legend,
  hint,
  value,
  onChange,
}: {
  legend: string;
  hint: string;
  value: SmartAdjustClaudeRunConfig;
  onChange: (next: SmartAdjustClaudeRunConfig) => void;
}): JSX.Element {
  return (
    <div className="grid gap-2">
      <div className="text-xs font-semibold text-slate-700">{legend}</div>
      <p className="text-[11px] text-slate-500 -mt-1">{hint}</p>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <label className="grid gap-1 text-sm">
          <span className="text-xs text-slate-600">模型</span>
          <select
            value={value.model}
            onChange={(e) =>
              onChange({
                ...value,
                model: e.target.value as SmartAdjustClaudeRunConfig['model'],
              })
            }
            className={SELECT_CLASS}
          >
            {MODEL_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
        </label>
        <label className="grid gap-1 text-sm">
          <span className="text-xs text-slate-600">Effort(推理強度)</span>
          <select
            value={value.effort}
            onChange={(e) =>
              onChange({
                ...value,
                effort: e.target.value as SmartAdjustClaudeRunConfig['effort'],
              })
            }
            className={SELECT_CLASS}
          >
            {EFFORT_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
        </label>
      </div>
    </div>
  );
}

function extractApiError(e: unknown, fallback: string): string {
  if (axios.isAxiosError(e)) {
    const data = e.response?.data as { error?: { message?: string } } | undefined;
    return data?.error?.message ?? e.message ?? fallback;
  }
  return e instanceof Error ? e.message : fallback;
}

export function SmartAdjustSettingsPage(): JSX.Element {
  const [view, setView] = useState<SmartAdjustIntegrationsView | null>(null);
  const [form, setForm] = useState<{
    redmineUrl: string;
    redmineProjectIdentifier: string;
    redmineApiKey: string;
    gitlabUrl: string;
    gitlabAccessToken: string;
    claude: SmartAdjustClaudeConfig;
  }>({
    redmineUrl: '',
    redmineProjectIdentifier: '',
    redmineApiKey: '',
    gitlabUrl: '',
    gitlabAccessToken: '',
    claude: { analyze: { model: '', effort: '' }, edit: { model: '', effort: '' } },
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [flash, setFlash] = useState<{ kind: 'ok' | 'err'; msg: string } | null>(null);

  const load = async (): Promise<void> => {
    setLoading(true);
    try {
      const intgRes = await apiClient.get<ApiSuccess<SmartAdjustIntegrationsView>>(
        '/admin/smart-adjust/integrations',
      );
      const v = intgRes.data.data;
      setView(v);
      setForm({
        redmineUrl: v.redmine.url,
        redmineProjectIdentifier: v.redmine.projectIdentifier,
        redmineApiKey: '',
        gitlabUrl: v.gitlab.url,
        gitlabAccessToken: '',
        claude: v.claude,
      });
    } catch (e) {
      setFlash({ kind: 'err', msg: extractApiError(e, '讀取設定失敗') });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void load();
  }, []);

  const onSave = async (): Promise<void> => {
    setSaving(true);
    setFlash(null);
    try {
      const res = await apiClient.put<ApiSuccess<SmartAdjustIntegrationsView>>(
        '/admin/smart-adjust/integrations',
        {
          redmine: {
            url: form.redmineUrl.trim(),
            projectIdentifier: form.redmineProjectIdentifier.trim(),
            apiKey: form.redmineApiKey,
          },
          gitlab: {
            url: form.gitlabUrl.trim(),
            accessToken: form.gitlabAccessToken,
          },
          claude: form.claude,
        },
      );
      setView(res.data.data);
      // 清空已存入 DB 的敏感欄位
      setForm((f) => ({ ...f, redmineApiKey: '', gitlabAccessToken: '' }));
      setFlash({ kind: 'ok', msg: '已儲存' });
    } catch (e) {
      setFlash({ kind: 'err', msg: extractApiError(e, '儲存失敗') });
    } finally {
      setSaving(false);
    }
  };

  return (
    <section className="grid gap-4 max-w-3xl">
      <h2 className="text-lg font-semibold inline-flex items-center gap-2">
        <span className="inline-flex items-center justify-center text-[11px] font-bold rounded-md bg-violet-100 text-violet-700 px-1.5 py-0.5">
          AI
        </span>
        我獨自升級設定
      </h2>
      <p className="text-xs text-slate-600 leading-relaxed">
        Redmine 與 GitLab 整合資訊,兩者皆已實際運作。
        <br />
        <strong className="text-slate-800">Redmine</strong>:升級任務會自動開工單、
        結束時回寫結果並關單;在需求表單填入工單網址,也會自動抓取該工單內容作為分析依據。
        <br />
        <strong className="text-slate-800">GitLab Access Token</strong>:C 級升級推送程式碼時
        走它做 HTTPS 認證(與部署用的唯讀 deploy key 分開)。
        <br />
        API key 與 token 均以加密欄位儲存,GET 時不外洩。
        <br />
        <strong className="text-emerald-700">
          升級分析與程式碼變更都由主機上的 Claude Code 執行(訂閱認證),不需要任何 AI API Key;
          下方只能選它用哪個模型與推理強度。
        </strong>
      </p>

      <div className="bg-white border rounded p-4 grid gap-4">
        {loading ? (
          <div className="text-sm text-slate-500">載入中…</div>
        ) : (
          <>
            <fieldset className="grid gap-2">
              <legend className="text-sm font-semibold text-slate-800">Redmine</legend>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <label className="grid gap-1 text-sm">
                  <span className="text-xs text-slate-600">URL</span>
                  <input
                    type="url"
                    value={form.redmineUrl}
                    onChange={(e) => setForm((f) => ({ ...f, redmineUrl: e.target.value }))}
                    placeholder="https://redmine.example.com"
                    className="border rounded px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
                  />
                </label>
                <label className="grid gap-1 text-sm">
                  <span className="text-xs text-slate-600">Project Identifier</span>
                  <input
                    type="text"
                    value={form.redmineProjectIdentifier}
                    onChange={(e) =>
                      setForm((f) => ({ ...f, redmineProjectIdentifier: e.target.value }))
                    }
                    placeholder="interview"
                    className="border rounded px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
                  />
                </label>
                <label className="grid gap-1 text-sm md:col-span-2">
                  <span className="text-xs text-slate-600">
                    API Key
                    {view?.redmine.apiKeyConfigured ? (
                      <span className="ml-1 text-emerald-700">(已設定)</span>
                    ) : (
                      <span className="ml-1 text-slate-400">(未設定)</span>
                    )}
                  </span>
                  <input
                    type="password"
                    value={form.redmineApiKey}
                    onChange={(e) => setForm((f) => ({ ...f, redmineApiKey: e.target.value }))}
                    placeholder={
                      view?.redmine.apiKeyConfigured ? '(留空 = 不變)' : '貼上 API key'
                    }
                    className="border rounded px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400 font-mono"
                  />
                  <span className="text-[11px] text-slate-400">
                    在 Redmine 個人設定 → API access keys 產生
                  </span>
                </label>
              </div>
            </fieldset>

            <fieldset className="grid gap-3">
              <legend className="text-sm font-semibold text-slate-800">GitLab / Gitea(C 級升級 git push 用)</legend>
              <p className="text-[11px] text-slate-500 -mt-1">
                C 級升級需要 AI-agent 把 commit push 回 git server 觸發 webhook redeploy。
                <strong className="text-slate-700">這把 token 是必要的</strong>——
                本專案部署用的 SSH deploy key 是<strong className="text-slate-700">唯讀</strong>的
                (推送會被 GitLab 拒絕),兩者刻意分離,部署金鑰外洩也無法竄改程式碼。
              </p>
              <label className="grid gap-1 text-sm">
                <span className="text-xs text-slate-600">Repo HTTPS URL(結尾要含 .git)</span>
                <input
                  type="url"
                  value={form.gitlabUrl}
                  onChange={(e) => setForm((f) => ({ ...f, gitlabUrl: e.target.value }))}
                  placeholder="https://gitlab.example.com/org/repo.git"
                  className="border rounded px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
                />
              </label>
              <label className="grid gap-1 text-sm">
                <span className="text-xs text-slate-600">
                  Personal Access Token
                  {view?.gitlab.accessTokenConfigured ? (
                    <span className="ml-1 text-emerald-700">(已設定)</span>
                  ) : (
                    <span className="ml-1 text-slate-400">(未設定)</span>
                  )}
                </span>
                <input
                  type="password"
                  value={form.gitlabAccessToken}
                  onChange={(e) =>
                    setForm((f) => ({ ...f, gitlabAccessToken: e.target.value }))
                  }
                  placeholder={
                    view?.gitlab.accessTokenConfigured ? '(留空 = 不變)' : '貼上 access token'
                  }
                  className="border rounded px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400 font-mono"
                />
                <span className="text-[11px] text-slate-400">
                  GitLab → User Settings → Access Tokens(scopes 至少 api / write_repository)
                </span>
              </label>
            </fieldset>

            <fieldset className="grid gap-3">
              <legend className="text-sm font-semibold text-slate-800">
                Claude Code 執行體(模型 / 推理強度)
              </legend>
              <p className="text-[11px] text-slate-500 -mt-1">
                兩者都留「預設」時行為與過去完全相同(由主機端的 Claude Code 自行決定)。
                模型只提供別名,會自動跟上該系列的最新版本。
                <strong className="text-slate-700">
                  effort 越高越花時間與訂閱額度
                </strong>
                ,額度用完時升級會直接失敗,請斟酌調整。
              </p>
              <ClaudeRunFields
                legend="升級分析"
                hint="唯讀讀取程式碼、產出執行計畫。每次送出需求都會跑。"
                value={form.claude.analyze}
                onChange={(next) =>
                  setForm((f) => ({ ...f, claude: { ...f.claude, analyze: next } }))
                }
              />
              <ClaudeRunFields
                legend="程式碼變更(C 級)"
                hint="實際改檔。分析階段的 dry-run 預覽與按下「開始升級」後的實作共用這一組。"
                value={form.claude.edit}
                onChange={(next) =>
                  setForm((f) => ({ ...f, claude: { ...f.claude, edit: next } }))
                }
              />
            </fieldset>

            <div className="flex items-center gap-2 pt-2 border-t">
              <button
                type="button"
                onClick={() => void onSave()}
                disabled={saving}
                className="bg-blue-600 text-white px-3 py-1.5 rounded text-sm hover:bg-blue-700 disabled:opacity-50"
              >
                {saving ? '儲存中…' : '儲存設定'}
              </button>
              {flash ? (
                <span
                  className={`text-xs ${flash.kind === 'ok' ? 'text-emerald-700' : 'text-red-700'}`}
                >
                  {flash.msg}
                </span>
              ) : null}
            </div>
          </>
        )}
      </div>
    </section>
  );
}
